// =============================================================================
// AGENT 2 — MISTRZ UI: PLANSZA GRY
// Orkiestruje DnD między TaskCard (sources) a WorkerSlot (targets).
// Koordynuje HTML5 Drag and Touch API dla mobile.
// =============================================================================

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import './GameBoard.css';
import { TaskCard } from '../TaskCard/TaskCard';
import { WorkerSlot } from '../WorkerSlot/WorkerSlot';
import { Timer } from '../Timer/Timer';
import { useGame } from '../../store/gameStore';
import type { Task, Worker, FinishMeta, FinishReason } from '../../types/models';
import { BrandLogo } from '../../ui';
import { COPY } from '../../copy/microcopy';
import { deriveTempoStage } from '../../store/tempoStage';

interface GameBoardProps {
  tasks: Task[];
  workers: Worker[];
  onFinishRequest: (reason: FinishReason, meta?: Partial<FinishMeta>) => void;
}

interface TouchDragState {
  taskId: string;
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
}

interface AssignmentAckState {
  type: 'success' | 'error';
  message: string;
}

const PRIORITY_LABELS: Record<Task['priority'], string> = {
  awaria: '🔴 AWARIA',
  pilne: '🟡 PILNE',
  normalne: '🔵 NORMALNE',
  rutynowe: '⚫ RUTYNOWE',
};

export function GameBoard({ tasks, workers, onFinishRequest }: GameBoardProps) {
  const { state, assignTaskToWorker } = useGame();
  const hasFinishedRef = useRef(false);
  const lastAssignmentRef = useRef<{ key: string; at: number } | null>(null);
  const ignoredAwariaByTaskIdRef = useRef<Record<string, boolean>>({});
  const excessTaskByTaskIdRef = useRef<Record<string, boolean>>({});
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [dragOverWorkerId, setDragOverWorkerId] = useState<string | null>(null);
  const [lastFeedbackWorkerId, setLastFeedbackWorkerId] = useState<string | null>(null);
  const [lastFeedbackType, setLastFeedbackType] = useState<'success' | 'error' | null>(null);
  const [assignmentAck, setAssignmentAck] = useState<AssignmentAckState | null>(null);
  const [touchDrag, setTouchDrag] = useState<TouchDragState | null>(null);
  const [revealedAwariaIds, setRevealedAwariaIds] = useState<string[]>([]);
  const tempoStage = deriveTempoStage(state.timeLeft);
  const showFinalAlert = tempoStage === 'final' && state.phase === 'playing';

  const mainQueue = useMemo(
    () => tasks.filter((task) => task.priority !== 'awaria'),
    [tasks],
  );
  const awariaQueue = useMemo(
    () => tasks.filter((task) => task.priority === 'awaria'),
    [tasks],
  );
  const unassignedMainTasks = mainQueue.filter(
    (task) => !state.assignments.find((assignment) => assignment.taskId === task.id),
  );
  const unassignedAwariaTasks = awariaQueue.filter(
    (task) => !state.assignments.find((assignment) => assignment.taskId === task.id),
  );
  const visibleAwariaTasks = unassignedAwariaTasks.filter((task) => revealedAwariaIds.includes(task.id));

  useEffect(() => {
    setRevealedAwariaIds([]);
    hasFinishedRef.current = false;
    ignoredAwariaByTaskIdRef.current = {};
    excessTaskByTaskIdRef.current = {};

    if (awariaQueue.length === 0) return undefined;

    const revealWindows: Array<[number, number]> = [
      [4000, 12000],
      [12000, 26000],
      [26000, 44000],
      [44000, 65000],
    ];

    const revealDelays = awariaQueue.map((task, index) => {
      const [minDelay, maxDelay] = revealWindows[Math.min(index, revealWindows.length - 1)];
      return {
        taskId: task.id,
        delay: minDelay + Math.floor((maxDelay - minDelay) / 2),
      };
    });

    const timers = revealDelays.map(({ taskId, delay }) =>
      window.setTimeout(() => {
        setRevealedAwariaIds((current) => (current.includes(taskId) ? current : [...current, taskId]));
      }, delay),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [awariaQueue]);

  useEffect(() => {
    if (!assignmentAck) return undefined;

    const timer = window.setTimeout(() => {
      setAssignmentAck(null);
      setLastFeedbackWorkerId(null);
      setLastFeedbackType(null);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [assignmentAck]);

  const collectFinishMeta = useCallback((): FinishMeta => ({
    ignoredAwariaByTaskId: { ...ignoredAwariaByTaskIdRef.current },
    excessTaskByTaskId: { ...excessTaskByTaskIdRef.current },
  }), []);

  const requestFinish = useCallback((reason: FinishReason) => {
    onFinishRequest(reason, {
      ignoredAwariaByTaskId: { ...ignoredAwariaByTaskIdRef.current },
      excessTaskByTaskId: { ...excessTaskByTaskIdRef.current },
    });
  }, [onFinishRequest]);

  const assignTaskWithPriorityPenalty = useCallback(
    (taskId: string, workerId: string): boolean => {
      if (state.phase !== 'playing') {
        setAssignmentAck({ type: 'error', message: COPY.game.assignmentAckError });
        setLastFeedbackWorkerId(workerId);
        setLastFeedbackType('error');
        return false;
      }

      const pickedTask = tasks.find((task) => task.id === taskId);
      if (!pickedTask) {
        setAssignmentAck({ type: 'error', message: COPY.game.assignmentAckError });
        setLastFeedbackWorkerId(workerId);
        setLastFeedbackType('error');
        return false;
      }

      const awariaIsVisible = visibleAwariaTasks.length > 0;
      const workerTasksExcludingCurrent = state.assignments.filter(
        (assignment) => assignment.workerId === workerId && assignment.taskId !== taskId,
      );
      const willExceedTaskLimit = workerTasksExcludingCurrent.length >= 5;
      const currentAssignment = state.assignments.find((assignment) => assignment.taskId === taskId);

      if (currentAssignment?.workerId === workerId) {
        setAssignmentAck({ type: 'success', message: COPY.game.assignmentAckSuccess });
        setLastFeedbackWorkerId(workerId);
        setLastFeedbackType('success');
        return true;
      }

      const dedupeKey = `${taskId}:${workerId}`;
      const now = Date.now();
      const duplicateBurst =
        lastAssignmentRef.current?.key === dedupeKey
        && now - lastAssignmentRef.current.at < 140;

      if (duplicateBurst) {
        setAssignmentAck({ type: 'success', message: COPY.game.assignmentAckSuccess });
        setLastFeedbackWorkerId(workerId);
        setLastFeedbackType('success');
        return true;
      }

      ignoredAwariaByTaskIdRef.current[taskId] = !!pickedTask && pickedTask.priority !== 'awaria' && awariaIsVisible;
      excessTaskByTaskIdRef.current[taskId] = willExceedTaskLimit;

      assignTaskToWorker(taskId, workerId);
      lastAssignmentRef.current = { key: dedupeKey, at: now };
      setAssignmentAck({ type: 'success', message: COPY.game.assignmentAckSuccess });
      setLastFeedbackWorkerId(workerId);
      setLastFeedbackType('success');
      return true;
    },
    [tasks, visibleAwariaTasks, state.assignments, state.phase, assignTaskToWorker],
  );

  useEffect(() => {
    if (tasks.length === 0) return;
    if (hasFinishedRef.current) return;

    if (state.assignments.length >= tasks.length) {
      hasFinishedRef.current = true;
      onFinishRequest('all-assigned', collectFinishMeta());
    }
  }, [state.assignments.length, tasks.length, onFinishRequest, collectFinishMeta]);

  // --- HTML5 Drag events ---
  const handleDragStart = useCallback((taskId: string) => {
    setDraggingTaskId(taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingTaskId(null);
    setDragOverWorkerId(null);
    setTouchDrag(null);
  }, []);

  const handleDragOver = useCallback((_e: React.DragEvent, workerId: string) => {
    setDragOverWorkerId(workerId);
  }, []);

  const handleDrop = useCallback(
    (workerId: string) => {
      if (draggingTaskId) {
        assignTaskWithPriorityPenalty(draggingTaskId, workerId);
      } else {
        setAssignmentAck({ type: 'error', message: COPY.game.assignmentAckError });
        setLastFeedbackWorkerId(workerId);
        setLastFeedbackType('error');
      }
      setDraggingTaskId(null);
      setDragOverWorkerId(null);
    },
    [draggingTaskId, assignTaskWithPriorityPenalty],
  );

  // --- Touch API (mobile) — uproszczony pointer-based DnD ---
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, taskId: string) => {
      // Tap drugi raz na ten sam kafelek wyłącza tryb przypisania.
      if (draggingTaskId === taskId) {
        setDraggingTaskId(null);
        setDragOverWorkerId(null);
        setTouchDrag(null);
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      const cardRect = (e.currentTarget as HTMLElement).getBoundingClientRect();

      setDraggingTaskId(taskId);
      setTouchDrag({
        taskId,
        x: touch.clientX,
        y: touch.clientY,
        offsetX: touch.clientX - cardRect.left,
        offsetY: touch.clientY - cardRect.top,
      });
    },
    [draggingTaskId],
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!draggingTaskId) return;

    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;

    setTouchDrag((prev) =>
      prev
        ? {
            ...prev,
            x: touch.clientX,
            y: touch.clientY,
          }
        : prev,
    );

    const target = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
    const slot = target?.closest('[data-worker-id]') as HTMLElement | null;
    setDragOverWorkerId(slot?.dataset.workerId ?? null);
  }, [draggingTaskId]);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      if (!touch || !draggingTaskId) {
        setTouchDrag(null);
        return;
      }
      const target = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
      const slot = target?.closest('[data-worker-id]') as HTMLElement | null;
      const workerId = slot?.dataset.workerId;

      if (workerId) {
        assignTaskWithPriorityPenalty(draggingTaskId, workerId);
        setDraggingTaskId(null);
      } else {
        setAssignmentAck({ type: 'error', message: COPY.game.assignmentAckError });
        setLastFeedbackType('error');
      }
      setTouchDrag(null);
      setDragOverWorkerId(null);
    },
    [draggingTaskId, assignTaskWithPriorityPenalty],
  );

  const handleTouchCancel = useCallback(() => {
    setDragOverWorkerId(null);
    setTouchDrag(null);
  }, []);

  const handleTapAssign = useCallback(
    (workerId: string) => {
      if (!draggingTaskId) {
        return;
      }
      assignTaskWithPriorityPenalty(draggingTaskId, workerId);
      setDraggingTaskId(null);
      setDragOverWorkerId(null);
    },
    [draggingTaskId, assignTaskWithPriorityPenalty],
  );

  const getAssignedTask = (workerId: string): Task | null => {
    const assignment = state.assignments.find((a) => a.workerId === workerId);
    if (!assignment) return null;
    const workerAssignments = state.assignments.filter((a) => a.workerId === workerId);
    const lastAssignment = workerAssignments[workerAssignments.length - 1] ?? assignment;
    return tasks.find((t) => t.id === lastAssignment.taskId) ?? null;
  };

  const touchGhostTask = touchDrag ? tasks.find((task) => task.id === touchDrag.taskId) ?? null : null;
  const topMainTask = unassignedMainTasks[0] ?? null;

  useEffect(() => {
    if (tasks.length === 0) return;
    if (state.assignments.length >= tasks.length) return;

    const hasAnyVisibleTask = !!topMainTask || visibleAwariaTasks.length > 0;
    if (!hasAnyVisibleTask && unassignedAwariaTasks.length > 0) {
      const nextAwariaId = unassignedAwariaTasks[0].id;
      setRevealedAwariaIds((current) => (
        current.includes(nextAwariaId) ? current : [...current, nextAwariaId]
      ));
    }
  }, [
    topMainTask,
    visibleAwariaTasks.length,
    unassignedAwariaTasks,
    state.assignments.length,
    tasks.length,
  ]);

  return (
    <div className={`game-board game-board--tempo-${tempoStage}`} data-tempo-stage={tempoStage}>
      <div className="game-board__header">
        <div className="game-board__brand">
          <BrandLogo tone="white" compact className="game-board__logo" />
        </div>
        <div className="game-board__timer-wrap">
          <Timer onTimeUp={() => requestFinish('time-up')} />
          <span className="game-board__hint">Przeciągnij zgłoszenie na właściwego pracownika</span>
        </div>
      </div>

      {showFinalAlert ? (
        <div className="game-board__final-alert" role="status" aria-live="polite">
          {COPY.game.finalAlert}
        </div>
      ) : null}

      {assignmentAck ? (
        <div
          className={`game-board__ack game-board__ack--${assignmentAck.type}`}
          role="status"
          aria-live="polite"
        >
          {assignmentAck.message}
        </div>
      ) : null}

      <div className="game-board__columns">
        <div className="game-board__column game-board__column--stacks">
          <div className="game-board__stack-group">
            {topMainTask ? (
              <TaskCard
                key={topMainTask.id}
                task={topMainTask}
                isDragging={draggingTaskId === topMainTask.id}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
              />
            ) : null}
          </div>

          {visibleAwariaTasks.length > 0 && (
            <div className="game-board__stack-group game-board__stack-group--alert">
              <div className="game-board__alert-stack">
                {visibleAwariaTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    isDragging={draggingTaskId === task.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchCancel}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="game-board__column">
          <div className="game-board__column-title">Pracownicy</div>
          {workers.map((worker) => (
            <WorkerSlot
              key={worker.id}
              worker={worker}
              assignedTask={getAssignedTask(worker.id)}
              taskCount={state.assignments.filter((a) => a.workerId === worker.id).length}
              isDragOver={dragOverWorkerId === worker.id}
              isTaskSelected={!!draggingTaskId}
              dropFeedback={
                lastFeedbackWorkerId === worker.id
                  ? lastFeedbackType
                  : null
              }
              onDragOver={handleDragOver}
              onDragLeave={() => setDragOverWorkerId(null)}
              onDrop={handleDrop}
              onTapAssign={handleTapAssign}
            />
          ))}
        </div>
      </div>

      <button
        className="game-board__finish-btn"
        onClick={() => requestFinish('manual')}
        disabled={state.assignments.length === 0}
      >
        Gotowe — pokaż wynik
      </button>

      {touchDrag && touchGhostTask && (
        <div
          className="task-card task-card--dragging task-card--touch-ghost"
          data-priority={touchGhostTask.priority}
          style={{
            left: touchDrag.x - touchDrag.offsetX,
            top: touchDrag.y - touchDrag.offsetY,
          }}
          aria-hidden
        >
          <div className="task-card__priority-pill" aria-hidden>
            {PRIORITY_LABELS[touchGhostTask.priority]}
          </div>
          <div className="task-card__title-wrap">
            <div className="task-card__title">{touchGhostTask.title}</div>
          </div>
          <div className="task-card__icon-shell" aria-hidden>
            <img className="task-card__icon" src={touchGhostTask.iconAsset} alt="" />
          </div>
        </div>
      )}
    </div>
  );
}
